import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  AttachFile as AttachFileIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  Pets as PetsIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Healing as HealingIcon,
  Receipt as ReceiptIcon,
  CreditCardOff as NoChipIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import DateField from './DateField';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Column {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default: string | null;
}

interface Cat {
  id: number;
  chip_id?: string;
  name?: string;
  sex?: string;
  age?: string;
  breed?: string;
  arrival_date?: string;
  departure_date?: string;
  municipality?: string;
  source?: string;
  address_where_found?: string;
  temporary_placement?: string;
  area?: string;
  sterilized?: string;
  notes?: string;
  fiv?: string;
  felv?: string;
}

interface MedicalEvent {
  id: number;
  cat_id: number;
  cat_name?: string;
  event_type?: string;
  event_date?: string;
  cost?: number;
  veterinary_name?: string;
  invoice_file_path?: string;
  notes?: string;
}

interface Stats {
  totalCats: number;
  male: number;
  female: number;
  sterilized: number;
  notSterilized: number;
  fivPositive: number;
  felvPositive: number;
}

const MainMenu: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addEventOpen, setAddEventOpen] = useState(false);

  const [catsColumns, setCatsColumns] = useState<Column[]>([]);
  const [medicalColumns, setMedicalColumns] = useState<Column[]>([]);
  const [catFormData, setCatFormData] = useState<any>({});
  const [eventFormData, setEventFormData] = useState<any>({});

  const [cats, setCats] = useState<Cat[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [selectedCat, setSelectedCat] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');

  // Statistics and warnings state
  const [stats, setStats] = useState<Stats>({
    totalCats: 0,
    male: 0,
    female: 0,
    sterilized: 0,
    notSterilized: 0,
    fivPositive: 0,
    felvPositive: 0,
  });
  const [catsWithoutChip, setCatsWithoutChip] = useState<Cat[]>([]);
  const [catsNotSterilized, setCatsNotSterilized] = useState<Cat[]>([]);
  const [eventsWithoutInvoice, setEventsWithoutInvoice] = useState<MedicalEvent[]>([]);

  // Upload invoice dialog state
  const [uploadInvoiceOpen, setUploadInvoiceOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<MedicalEvent | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);


  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Wine names for cat name suggestions
  const wineNames = [
    'Nebbiolo', 'Sangiovese', 'Barbera', 'Montepulciano', 'Primitivo',
    'Nero d\'Avola', 'Aglianico', 'Corvina', 'Dolcetto', 'Lambrusco',
    'Moscato', 'Trebbiano', 'Vermentino', 'Garganega', 'Prosecco',
    'Pinot Grigio', 'Vernaccia', 'Verdicchio', 'Malvasia', 'Greco',
    'Barolo', 'Barbaresco', 'Brunello', 'Chianti', 'Amarone',
    'Valpolicella', 'Taurasi', 'Sagrantino', 'Franciacorta', 'Gavi',
    'Cabernet Sauvignon', 'Merlot', 'Pinot Noir', 'Chardonnay', 'Syrah',
    'Grenache', 'Sauvignon Blanc', 'Viognier', 'Chenin Blanc', 'Riesling',
    'Chablis', 'Meursault', 'Pommard', 'Volnay', 'Beaune',
    'Châteauneuf-du-Pape', 'Hermitage', 'Côte-Rôtie', 'Gigondas',
    'Sancerre', 'Pouilly-Fumé', 'Chinon', 'Vouvray', 'Champagne',
    'Tempranillo', 'Garnacha', 'Albariño', 'Verdejo', 'Rioja',
  ];

  const suggestCatName = () => {
    const randomIndex = Math.floor(Math.random() * wineNames.length);
    return wineNames[randomIndex];
  };

  // Load data on mount
  useEffect(() => {
    loadCatsColumns();
    loadMedicalColumns();
    loadAllCats();
    loadMunicipalities();
    loadEventsWithoutInvoice();
  }, []);

  // Compute statistics when cats data changes
  useEffect(() => {
    if (cats.length > 0) {
      computeStatistics();
      computeWarnings();
    }
  }, [cats]);

  const loadCatsColumns = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/cats/columns`);
      setCatsColumns(response.data);
    } catch (error) {
      console.error('Error loading cats columns:', error);
    }
  };

  const loadMedicalColumns = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/medical/columns`);
      setMedicalColumns(response.data);
    } catch (error) {
      console.error('Error loading medical columns:', error);
    }
  };

  const loadAllCats = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/cats/data?limit=1000`);
      const catsData = response.data.rows.map((row: any[]) => {
        const cat: any = {};
        response.data.columns.forEach((col: string, i: number) => {
          cat[col] = row[i];
        });
        return cat;
      });
      setCats(catsData);
    } catch (error) {
      console.error('Error loading cats:', error);
    }
  };

  const loadMunicipalities = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/municipalities/data?limit=1000`);
      const municipalitiesData = response.data.rows.map((row: any[]) => row[0]);
      setMunicipalities(municipalitiesData.sort());
    } catch (error) {
      console.error('Error loading municipalities:', error);
    }
  };

  const loadEventsWithoutInvoice = async () => {
    try {
      const response = await api.get(`${API_URL}/api/tables/public/medical/data?limit=1000`);
      const eventsData = response.data.rows.map((row: any[]) => {
        const event: any = {};
        response.data.columns.forEach((col: string, i: number) => {
          event[col] = row[i];
        });
        return event;
      });

      // Filter events without invoice
      const eventsNoInvoice = eventsData.filter(
        (event: MedicalEvent) => !event.invoice_file_path || event.invoice_file_path === ''
      );
      setEventsWithoutInvoice(eventsNoInvoice);
    } catch (error) {
      console.error('Error loading medical events:', error);
    }
  };

  const computeStatistics = () => {
    const newStats: Stats = {
      totalCats: cats.length,
      male: cats.filter(c => c.sex === 'M').length,
      female: cats.filter(c => c.sex === 'F').length,
      sterilized: cats.filter(c => c.sterilized === 'Y').length,
      notSterilized: cats.filter(c => c.sterilized !== 'Y').length,
      fivPositive: cats.filter(c => c.fiv === 'Y').length,
      felvPositive: cats.filter(c => c.felv === 'Y').length,
    };
    setStats(newStats);
  };

  const computeWarnings = () => {
    setCatsWithoutChip(cats.filter(c => !c.chip_id));
    setCatsNotSterilized(cats.filter(c => c.sterilized === 'N'));
  };

  // Get cat name by ID for events display
  const getCatName = (catId: number): string => {
    const cat = cats.find(c => c.id === catId);
    return cat?.name || `Cat #${catId}`;
  };

  // Get cat chip_id by ID for events display
  const getCatChipId = (catId: number): string => {
    const cat = cats.find(c => c.id === catId);
    return cat?.chip_id || t('cat.missing');
  };

  const handleAddCat = async () => {
    try {
      let photoPath = null;
      if (selectedPhoto) {
        photoPath = await handlePhotoUpload();
        if (!photoPath) {
          setMessage({ type: 'error', text: 'Photo upload failed. Please try again.' });
          return;
        }
      }

      const dataToSubmit = {
        ...catFormData,
        ...(photoPath && { photo_file_path: photoPath })
      };
      if (dataToSubmit.sterilized === 'U') dataToSubmit.sterilized = null;

      await api.post(`${API_URL}/api/records`, {
        table: 'cats',
        schema: 'public',
        data: dataToSubmit,
      });
      setMessage({ type: 'success', text: 'Cat added successfully!' });
      setCatFormData({});
      setSelectedPhoto(null);
      setPhotoPreview('');
      setAddCatOpen(false);
      loadAllCats();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return null;

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post(`${API_URL}/api/upload/invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.file_path;
    } catch (error: any) {
      setMessage({ type: 'error', text: `File upload failed: ${error.response?.data?.detail || error.message}` });
      return null;
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedPhoto(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto) return null;

    try {
      const formData = new FormData();
      formData.append('file', selectedPhoto);

      const response = await api.post(`${API_URL}/api/upload/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.file_path;
    } catch (error: any) {
      setMessage({ type: 'error', text: `Photo upload failed: ${error.response?.data?.detail || error.message}` });
      return null;
    }
  };

  const handleUploadInvoice = async () => {
    if (!selectedEvent || !invoiceFile) return;

    try {
      // Upload the file
      const formData = new FormData();
      formData.append('file', invoiceFile);

      const uploadResponse = await api.post(`${API_URL}/api/upload/invoice`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const filePath = uploadResponse.data.file_path;

      // Update the medical event with the invoice path
      await api.put(`${API_URL}/api/records`, {
        table: 'medical',
        schema: 'public',
        record_id: selectedEvent.id,
        data: { invoice_file_path: filePath },
      });

      setMessage({ type: 'success', text: 'Invoice uploaded successfully!' });
      setUploadInvoiceOpen(false);
      setSelectedEvent(null);
      setInvoiceFile(null);
      loadEventsWithoutInvoice();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    }
  };

  const handleAddEvent = async () => {
    try {
      const eventType = eventFormData.event_type;
      const catId = eventFormData.cat_id;

      if ((eventType === 'Sterilization') && catId) {
        const checkResponse = await api.post(`${API_URL}/api/search`, {
          table: 'medical',
          schema: 'public',
          search_column: 'cat_id',
          search_value: catId.toString(),
          limit: 100,
        });

        const existingEvents = checkResponse.data.rows.map((row: any[]) => {
          const result: any = {};
          checkResponse.data.columns.forEach((col: string, i: number) => {
            result[col] = row[i];
          });
          return result;
        });

        if (eventType === 'Sterilization') {
          const hasSterilization = existingEvents.some((event: any) => event.event_type === 'Sterilization');
          if (hasSterilization) {
            setMessage({
              type: 'error',
              text: 'This cat already has a Sterilization event recorded.'
            });
            return;
          }
        }
      }

      let filePath = null;
      if (selectedFile) {
        filePath = await handleFileUpload();
        if (!filePath) {
          return;
        }
      }

      const eventData = { ...eventFormData };
      if (filePath) {
        eventData.invoice_file_path = filePath;
      }

      await api.post(`${API_URL}/api/records`, {
        table: 'medical',
        schema: 'public',
        data: eventData,
      });

      setMessage({ type: 'success', text: 'Medical event added successfully!' });
      setEventFormData({});
      setSelectedFile(null);
      setAddEventOpen(false);
      setSelectedCat(null);
      loadAllCats();
      loadEventsWithoutInvoice();
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Error: ${error.response?.data?.detail || error.message}` });
    }
  };


  // Compact form field renderer for Add New Cat dialog
  const renderCatField = (fieldName: string, label: string, options?: { type?: string; select?: { value: string; label: string }[]; multiline?: boolean; rows?: number; suggestName?: boolean }) => {
    if (options?.select) {
      return (
        <FormControl fullWidth size="small" key={fieldName}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={catFormData[fieldName] || options.select[0]?.value || ''}
            label={label}
            onChange={(e) => setCatFormData({ ...catFormData, [fieldName]: e.target.value })}
          >
            {options.select.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (fieldName === 'age') {
      const ageValue = catFormData[fieldName] || '';
      const parts = ageValue.split('/');
      const month = parts[0] || '';
      const year = parts[1] || '';
      return (
        <Box key={fieldName} sx={{ display: 'flex', gap: 1 }}>
          <FormControl sx={{ width: '45%' }} size="small">
            <InputLabel>{t('cat.birthMonth')}</InputLabel>
            <Select
              value={month}
              label={t('cat.birthMonth')}
              onChange={(e) => setCatFormData({ ...catFormData, [fieldName]: `${e.target.value}/${year}` })}
            >
              <MenuItem value=""><em>{t('common.month')}</em></MenuItem>
              {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{ width: '55%' }}
            size="small"
            label={t('cat.birthYear')}
            placeholder="YYYY"
            value={year}
            onChange={(e) => {
              const newYear = e.target.value.replace(/\D/g, '').slice(0, 4);
              setCatFormData({ ...catFormData, [fieldName]: `${month}/${newYear}` });
            }}
            inputProps={{ maxLength: 4 }}
          />
        </Box>
      );
    }

    return (
      <TextField
        key={fieldName}
        fullWidth
        size="small"
        label={label}
        type={options?.type || 'text'}
        value={catFormData[fieldName] || ''}
        onChange={(e) => setCatFormData({ ...catFormData, [fieldName]: e.target.value })}
        InputLabelProps={options?.type === 'date' ? { shrink: true } : undefined}
        multiline={options?.multiline}
        rows={options?.rows}
        InputProps={options?.suggestName ? {
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={t('cat.suggestName')}>
                <IconButton
                  onClick={() => setCatFormData({ ...catFormData, name: suggestCatName() })}
                  edge="end"
                  size="small"
                  sx={{ color: '#667eea' }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        } : undefined}
      />
    );
  };

  const renderEventFormField = (column: Column) => {
    if (column.column_name === 'invoice_file_path' || column.column_name === 'id') {
      return null;
    }

    if (column.column_name === 'cat_id') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name} required>
          <InputLabel>{t('cat.cat')}</InputLabel>
          <Select
            value={eventFormData[column.column_name] || ''}
            label={t('cat.cat')}
            onChange={(e) => setEventFormData({ ...eventFormData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('cat.selectCat')}</em></MenuItem>
            {cats.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name || t('cat.unnamed')} (ID: {cat.id}, Chip: {cat.chip_id || t('cat.missing')})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.column_name === 'cost') {
      return (
        <TextField
          key={column.column_name}
          fullWidth
          margin="normal"
          label={t('medical.costEuro')}
          type="number"
          inputProps={{ step: '0.01', min: '0' }}
          value={eventFormData[column.column_name] || ''}
          onChange={(e) => setEventFormData({ ...eventFormData, [column.column_name]: e.target.value })}
        />
      );
    }

    if (column.column_name === 'veterinary_name') {
      return (
        <TextField
          key={column.column_name}
          fullWidth
          margin="normal"
          label={t('medical.veterinaryName')}
          value={eventFormData[column.column_name] || ''}
          onChange={(e) => setEventFormData({ ...eventFormData, [column.column_name]: e.target.value })}
        />
      );
    }

    if (column.column_name === 'event_type') {
      return (
        <FormControl fullWidth margin="normal" key={column.column_name} required>
          <InputLabel>{t('medical.eventType')}</InputLabel>
          <Select
            value={eventFormData[column.column_name] || ''}
            label={t('medical.eventType')}
            onChange={(e) => setEventFormData({ ...eventFormData, [column.column_name]: e.target.value })}
          >
            <MenuItem value=""><em>{t('medical.selectEventType')}</em></MenuItem>
            <MenuItem value="Vaccine">{t('medical.eventTypes.vaccine')}</MenuItem>
            <MenuItem value="Sterilization">{t('medical.eventTypes.sterilization')}</MenuItem>
            <MenuItem value="Treatment">{t('medical.eventTypes.treatment')}</MenuItem>
            <MenuItem value="Delivery">{t('medical.eventTypes.delivery')}</MenuItem>
            <MenuItem value="Surgery">{t('medical.eventTypes.surgery')}</MenuItem>
            <MenuItem value="Checkup">{t('medical.eventTypes.checkup')}</MenuItem>
            <MenuItem value="test_fiv_felv">{t('medical.eventTypes.testFivFelv')}</MenuItem>
            <MenuItem value="Travel Expenses">{t('medical.eventTypes.travelExpenses')}</MenuItem>
            <MenuItem value="Other">{t('medical.eventTypes.other')}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column.data_type === 'date' || column.column_name.includes('_date')) {
      return (
        <DateField
          key={column.column_name}
          label={column.column_name}
          value={eventFormData[column.column_name] || ''}
          onChange={(v) => setEventFormData({ ...eventFormData, [column.column_name]: v })}
        />
      );
    }

    return (
      <TextField
        key={column.column_name}
        fullWidth
        margin="normal"
        label={column.column_name}
        value={eventFormData[column.column_name] || ''}
        onChange={(e) => setEventFormData({ ...eventFormData, [column.column_name]: e.target.value })}
      />
    );
  };

  // Statistics Card Component
  const StatCard: React.FC<{ icon: React.ReactNode; title: string; value: number | string; color: string; onClick?: () => void }> = ({ icon, title, value, color, onClick }) => (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        height: '100%',
        background: 'white',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 8px',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h4" fontWeight={700} color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 3
    }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 1,
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {t('dashboard.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 300,
              mb: 2
            }}
          >
            {t('dashboard.subtitle')}
          </Typography>
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {/* Statistics Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'white',
              mb: 2,
              textAlign: 'center',
            }}
          >
            {t('dashboard.shelterStatistics')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<PetsIcon sx={{ color: 'white', fontSize: 24 }} />}
                title={t('dashboard.totalCats')}
                value={stats.totalCats}
                color="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                onClick={() => navigate('/cats-list')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<MaleIcon sx={{ color: 'white', fontSize: 24 }} />}
                title={t('dashboard.male')}
                value={stats.male}
                color="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                onClick={() => navigate('/cats-list?filter=sex:M')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<FemaleIcon sx={{ color: 'white', fontSize: 24 }} />}
                title={t('dashboard.female')}
                value={stats.female}
                color="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                onClick={() => navigate('/cats-list?filter=sex:F')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<HealingIcon sx={{ color: 'white', fontSize: 24 }} />}
                title={t('dashboard.notSterilized')}
                value={stats.notSterilized}
                color="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                onClick={() => navigate('/cats-list?filter=sterilized:N')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>FIV+</Typography>}
                title={t('dashboard.fivPositive')}
                value={stats.fivPositive}
                color="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                onClick={() => navigate('/cats-list?filter=fiv:Y')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <StatCard
                icon={<Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>FeLV+</Typography>}
                title={t('dashboard.felvPositive')}
                value={stats.felvPositive}
                color="linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)"
                onClick={() => navigate('/cats-list?filter=felv:Y')}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Warnings Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'white',
              mb: 2,
              textAlign: 'center',
            }}
          >
            {t('dashboard.warningsAlerts')}
          </Typography>

          {/* Cats Without Chip ID */}
          <Accordion
            sx={{
              mb: 1,
              borderRadius: '12px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: catsWithoutChip.length > 0 ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' : '#f5f5f5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <NoChipIcon color={catsWithoutChip.length > 0 ? 'error' : 'disabled'} />
                <Typography fontWeight={600}>
                  {t('warnings.catsWithoutChip')}
                </Typography>
                <Chip
                  label={catsWithoutChip.length}
                  size="small"
                  color={catsWithoutChip.length > 0 ? 'error' : 'default'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {catsWithoutChip.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell>{t('table.id')}</TableCell>
                        <TableCell>{t('cat.name')}</TableCell>
                        <TableCell>{t('cat.arrivalDate')}</TableCell>
                        <TableCell>{t('table.action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {catsWithoutChip.map((cat) => (
                        <TableRow key={cat.id} hover>
                          <TableCell>{cat.id}</TableCell>
                          <TableCell>{cat.name || t('cat.unnamed')}</TableCell>
                          <TableCell>{cat.arrival_date || '-'}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/cat-record/${cat.id}`)}
                              sx={{ textTransform: 'none' }}
                            >
                              {t('common.view')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">{t('warnings.allCatsHaveChips')}</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Cats Not Sterilized */}
          <Accordion
            sx={{
              mb: 1,
              borderRadius: '12px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: catsNotSterilized.length > 0 ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' : '#f5f5f5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HealingIcon color={catsNotSterilized.length > 0 ? 'warning' : 'disabled'} />
                <Typography fontWeight={600}>
                  {t('warnings.catsNotSterilized')}
                </Typography>
                <Chip
                  label={catsNotSterilized.length}
                  size="small"
                  color={catsNotSterilized.length > 0 ? 'warning' : 'default'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {catsNotSterilized.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell>{t('cat.chipId')}</TableCell>
                        <TableCell>{t('cat.name')}</TableCell>
                        <TableCell>{t('cat.sex')}</TableCell>
                        <TableCell>{t('cat.arrivalDate')}</TableCell>
                        <TableCell>{t('table.action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {catsNotSterilized.map((cat) => (
                        <TableRow key={cat.id} hover>
                          <TableCell>{cat.chip_id || t('cat.missing')}</TableCell>
                          <TableCell>{cat.name || t('cat.unnamed')}</TableCell>
                          <TableCell>{cat.sex || '-'}</TableCell>
                          <TableCell>{cat.arrival_date || '-'}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/cat-record/${cat.id}`)}
                              sx={{ textTransform: 'none' }}
                            >
                              {t('common.view')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">{t('warnings.allCatsSterilized')}</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Medical Events Without Invoice */}
          <Accordion
            sx={{
              mb: 1,
              borderRadius: '12px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: eventsWithoutInvoice.length > 0 ? 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' : '#f5f5f5',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ReceiptIcon color={eventsWithoutInvoice.length > 0 ? 'info' : 'disabled'} />
                <Typography fontWeight={600}>
                  {t('warnings.eventsWithoutInvoice')}
                </Typography>
                <Chip
                  label={eventsWithoutInvoice.length}
                  size="small"
                  color={eventsWithoutInvoice.length > 0 ? 'info' : 'default'}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {eventsWithoutInvoice.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f5f5' }}>
                        <TableCell>{t('cat.chipId')}</TableCell>
                        <TableCell>{t('cat.name')}</TableCell>
                        <TableCell>{t('medical.eventType')}</TableCell>
                        <TableCell>{t('medical.eventDate')}</TableCell>
                        <TableCell>{t('medical.cost')}</TableCell>
                        <TableCell>{t('table.action')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eventsWithoutInvoice.map((event) => (
                        <TableRow key={event.id} hover>
                          <TableCell>{getCatChipId(event.cat_id)}</TableCell>
                          <TableCell>{getCatName(event.cat_id)}</TableCell>
                          <TableCell>{event.event_type || '-'}</TableCell>
                          <TableCell>{event.event_date || '-'}</TableCell>
                          <TableCell>{event.cost ? `€${event.cost}` : '-'}</TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AttachFileIcon />}
                              onClick={() => {
                                setSelectedEvent(event);
                                setUploadInvoiceOpen(true);
                              }}
                              sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                }
                              }}
                            >
                              {t('medical.uploadInvoice')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">{t('warnings.allEventsHaveInvoices')}</Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'white',
              mb: 2,
              textAlign: 'center',
            }}
          >
            {t('dashboard.quickActions')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setAddCatOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                }
              }}
            >
              {t('cat.addCat')}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<EventIcon />}
              onClick={() => {
                setEventFormData({ event_date: getTodayDate() });
                setAddEventOpen(true);
              }}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
                }
              }}
            >
              {t('medical.addEvent')}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Add Cat Dialog */}
      <Dialog
        open={addCatOpen}
        onClose={() => setAddCatOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700 }}>
          {t('cat.addNewCat')}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', pb: 1 }}>
          {/* Identity Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1.5 }}>
            {t('cat.identity')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderCatField('chip_id', t('cat.chipId'))}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('name', t('cat.name'), { suggestName: true })}
            </Grid>
            <Grid item xs={4}>
              {renderCatField('sex', t('cat.sex'), { select: [{ value: '', label: t('common.none') }, { value: 'M', label: 'M' }, { value: 'F', label: 'F' }] })}
            </Grid>
            <Grid item xs={8}>
              {renderCatField('age', t('cat.age'))}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('breed', t('cat.breed'))}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('color', t('cat.color'))}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Location & Source Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1.5 }}>
            {t('cat.locationSource')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderCatField('source', t('cat.source'), { select: [{ value: '', label: t('common.none') }, { value: 'ASL', label: t('cat.sources.asl') }, { value: 'Private', label: t('cat.sources.private') }, { value: 'Municipality', label: t('cat.sources.municipality') }, { value: 'Referente di Colonia', label: t('cat.sources.referenteDiColonia') }] })}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('municipality', t('cat.municipality'), { select: [{ value: '', label: t('common.none') }, ...municipalities.map(m => ({ value: m, label: m }))] })}
            </Grid>
            <Grid item xs={12}>
              {renderCatField('referente_di_colonia', t('cat.referenteDiColonia'))}
            </Grid>
            <Grid item xs={12}>
              {renderCatField('address_where_found', t('cat.addressWhereFound'), { multiline: true, rows: 2 })}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('temporary_placement', t('cat.temporaryPlacement'))}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('area', t('cat.area'))}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Health Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1.5 }}>
            {t('cat.health')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderCatField('initial_health_status', t('cat.initialHealthStatus'))}
            </Grid>
            <Grid item xs={4}>
              {renderCatField('sterilized', t('cat.sterilized'), { select: [{ value: 'U', label: t('common.unknown') }, { value: 'N', label: t('common.no') }, { value: 'Y', label: t('common.yes') }] })}
            </Grid>
            <Grid item xs={4}>
              {renderCatField('fiv', t('cat.fiv'), { select: [{ value: 'Unknown', label: t('common.unknown') }, { value: 'Y', label: t('common.yes') }, { value: 'N', label: t('common.no') }] })}
            </Grid>
            <Grid item xs={4}>
              {renderCatField('felv', t('cat.felv'), { select: [{ value: 'Unknown', label: t('common.unknown') }, { value: 'Y', label: t('common.yes') }, { value: 'N', label: t('common.no') }] })}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Dates Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1.5 }}>
            {t('cat.dates')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {renderCatField('arrival_date', t('cat.arrivalDate'), { type: 'date' })}
            </Grid>
            <Grid item xs={6}>
              {renderCatField('departure_date', t('cat.departureDate'), { type: 'date' })}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Notes & Photo Section */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#667eea', mb: 1.5 }}>
            {t('cat.notesPhoto')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderCatField('notes', t('cat.notes'), { multiline: true, rows: 2 })}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="cat-photo-upload"
                  type="file"
                  onChange={handlePhotoChange}
                />
                <label htmlFor="cat-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('cat.choosePhoto')}
                  </Button>
                </label>
                {selectedPhoto && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {photoPreview && (
                      <Box
                        component="img"
                        src={photoPreview}
                        alt="Preview"
                        sx={{ width: 48, height: 48, borderRadius: 1.5, objectFit: 'cover' }}
                      />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {selectedPhoto.name}
                    </Typography>
                  </Box>
                )}
                {!selectedPhoto && (
                  <Typography variant="body2" color="text.secondary">{t('common.optional')}</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setAddCatOpen(false);
              setSelectedPhoto(null);
              setPhotoPreview('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddCat}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            {t('cat.addCat')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog
        open={addEventOpen}
        onClose={() => setAddEventOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle>{t('medical.addMedicalEvent')}{selectedCat && ` ${t('medical.addMedicalEventFor')} ${selectedCat.name || 'Cat #' + selectedCat.id}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {medicalColumns.find(col => col.column_name === 'cat_id') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'cat_id')!)}

            {medicalColumns.find(col => col.column_name === 'event_type') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'event_type')!)}

            {medicalColumns.find(col => col.column_name === 'event_date') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'event_date')!)}

            {medicalColumns.find(col => col.column_name === 'cost') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'cost')!)}

            {medicalColumns.find(col => col.column_name === 'veterinary_name') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'veterinary_name')!)}

            {medicalColumns.find(col => col.column_name === 'notes') &&
              renderEventFormField(medicalColumns.find(col => col.column_name === 'notes')!)}

            {/* File Upload Section */}
            <Box sx={{ mt: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                {t('medical.invoiceUploadOptional')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {t('medical.uploadDocument')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  color="primary"
                  sx={{ flexGrow: 1 }}
                >
                  {t('common.browseFiles')}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setSelectedFile(null)}
                  >
                    {t('common.clear')}
                  </Button>
                )}
              </Box>

              {selectedFile && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" fontWeight="medium" color="primary">
                    {t('common.fileSelected')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.size')}: {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}

              {!selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                  {t('common.noFileSelected')}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddEventOpen(false);
              setSelectedCat(null);
              setEventFormData({});
              setSelectedFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddEvent}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(240,147,251,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
              }
            }}
          >
            {t('medical.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Invoice Dialog */}
      <Dialog
        open={uploadInvoiceOpen}
        onClose={() => {
          setUploadInvoiceOpen(false);
          setSelectedEvent(null);
          setInvoiceFile(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102,126,234,0.25)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700 }}>
          {t('medical.uploadInvoice')}
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          {selectedEvent && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('medical.medicalEventDetails')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('cat.cat')}</Typography>
                    <Typography variant="body1" fontWeight={600}>{getCatName(selectedEvent.cat_id)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('medical.eventType')}</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedEvent.event_type || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('medical.eventDate')}</Typography>
                    <Typography variant="body1">{selectedEvent.event_date || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('medical.cost')}</Typography>
                    <Typography variant="body1">{selectedEvent.cost ? `€${selectedEvent.cost}` : '-'}</Typography>
                  </Grid>
                  {selectedEvent.veterinary_name && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">{t('medical.veterinary')}</Typography>
                      <Typography variant="body1">{selectedEvent.veterinary_name}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}

          <Box sx={{ p: 2, border: '2px dashed #667eea', borderRadius: 2, bgcolor: '#f9f9ff', textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600} color="#667eea">
              {t('medical.selectInvoiceFile')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {t('medical.supportedFormats')}
            </Typography>

            <input
              accept=".pdf,.png,.jpg,.jpeg"
              style={{ display: 'none' }}
              id="invoice-upload"
              type="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files[0]) {
                  setInvoiceFile(e.target.files[0]);
                }
              }}
            />
            <label htmlFor="invoice-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<AttachFileIcon />}
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  }
                }}
              >
                {t('common.browseFiles')}
              </Button>
            </label>

            {invoiceFile && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', display: 'inline-block' }}>
                <Typography variant="body2" fontWeight="medium" color="primary">
                  {invoiceFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('common.size')}: {(invoiceFile.size / 1024).toFixed(2)} KB
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setInvoiceFile(null)}
                  sx={{ ml: 1, textTransform: 'none' }}
                >
                  {t('common.remove')}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setUploadInvoiceOpen(false);
              setSelectedEvent(null);
              setInvoiceFile(null);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUploadInvoice}
            variant="contained"
            disabled={!invoiceFile}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              },
              '&:disabled': {
                background: '#ccc',
              }
            }}
          >
            {t('medical.uploadInvoice')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MainMenu;
